import { API_BASE, apiDelete, apiGet, apiPost, apiPut } from "@/lib/api";
import { getToken } from "./auth";

function token(): string | undefined {
  return getToken() ?? undefined;
}

// ---------- Categorias ----------

export interface Category {
  id: string;
  slug: string;
  name: string;
}

interface CategoryListResponse {
  ok: boolean;
  count?: number;
  data?: Category[];
}

export async function listCategories(): Promise<Category[]> {
  const data = await apiGet<CategoryListResponse>("/functions/v1/get-categories");
  return data.data ?? [];
}

// ---------- Produtos ----------

export interface ProductSummary {
  id: string;
  slug: string;
  title: string;
  short_title: string;
  description: string;
  main_image: string;
  has_customization: boolean;
  is_active: boolean;
  sort_order: number;
  category_id: string | null;
  category_name: string | null;
  category_slug: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string | null;
}

export interface ProductImage {
  id?: string;
  url: string;
  alt: string | null;
  sort_order?: number;
}

export interface ProductDetail extends ProductSummary {
  long_description: string;
  features: string[];
  applications: string[];
  images: ProductImage[];
}

export interface ProductInput {
  id?: string;
  slug: string;
  title: string;
  short_title: string;
  category_id: string | null;
  description: string;
  long_description: string;
  main_image: string;
  has_customization: boolean;
  is_active: boolean;
  sort_order: number;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string | null;
  features: string[];
  applications: string[];
  images: Array<{ url: string; alt: string | null }>;
}

interface ProductListResponse {
  ok: boolean;
  count?: number;
  data?: ProductSummary[];
}

interface ProductDetailResponse {
  ok: boolean;
  data: ProductDetail;
}

export async function listProducts(): Promise<ProductSummary[]> {
  const data = await apiGet<ProductListResponse>("/functions/v1/admin-products", token());
  return data.data ?? [];
}

export async function getProduct(id: string): Promise<ProductDetail> {
  const data = await apiGet<ProductDetailResponse>(
    `/functions/v1/admin-products?id=${encodeURIComponent(id)}`,
    token(),
  );
  return data.data;
}

export async function createProduct(input: ProductInput): Promise<void> {
  await apiPost("/functions/v1/admin-products", input, token());
  logActivity("produto.criar", "produtos", input.title);
}

export async function updateProduct(input: ProductInput): Promise<void> {
  await apiPut("/functions/v1/admin-products", input, token());
  logActivity("produto.editar", "produtos", input.title);
}

export async function deleteProduct(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-products?id=${encodeURIComponent(id)}`, token());
  logActivity("produto.excluir", "produtos", id);
}

// ---------- Empresa / Configurações ----------

export interface Company {
  id?: number;
  name: string;
  short_name: string;
  address: string;
  phone: string;
  whatsapp: string;
  whatsapp_number: string;
  email: string;
  facebook: string | null;
  hours: string;
  since_year: number;
}

export interface SocialLink {
  id?: number;
  platform: string;
  url: string;
  icon?: string | null;
  sort_order?: number;
  is_active: boolean;
}

export interface SettingsData {
  company: Company | null;
  social: SocialLink[];
}

interface SettingsResponse {
  ok: boolean;
  data: SettingsData;
}

export async function getSettings(): Promise<SettingsData> {
  const data = await apiGet<SettingsResponse>("/functions/v1/admin-settings", token());
  return data.data;
}

export async function updateSettings(input: {
  company: Company;
  social: SocialLink[];
}): Promise<SettingsData> {
  const data = await apiPut<SettingsResponse>("/functions/v1/admin-settings", input, token());
  logActivity("empresa.editar", "empresa", input.company.name);
  return data.data;
}

// ---------- Galeria ----------

export interface GalleryCategory {
  id: number;
  name: string;
  sort_order: number;
}

export interface GalleryItem {
  id: string;
  url: string;
  alt: string;
  caption: string | null;
  category_id: number | null;
  category_name: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface GalleryInput {
  id?: string;
  url: string;
  alt: string;
  caption: string | null;
  category_id: number | null;
  sort_order: number;
  is_active: boolean;
}

interface GalleryListResponse {
  ok: boolean;
  count?: number;
  data?: GalleryItem[];
  categories?: GalleryCategory[];
}

export async function listGallery(): Promise<{ items: GalleryItem[]; categories: GalleryCategory[] }> {
  const data = await apiGet<GalleryListResponse>("/functions/v1/admin-gallery", token());
  return { items: data.data ?? [], categories: data.categories ?? [] };
}

export async function createGallery(input: GalleryInput): Promise<void> {
  await apiPost("/functions/v1/admin-gallery", input, token());
  logActivity("foto.criar", "galeria", input.alt);
}

export async function updateGallery(input: GalleryInput): Promise<void> {
  await apiPut("/functions/v1/admin-gallery", input, token());
  logActivity("foto.editar", "galeria", input.alt);
}

export async function deleteGallery(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-gallery?id=${encodeURIComponent(id)}`, token());
  logActivity("foto.excluir", "galeria", id);
}

// Salva a nova ordem das fotos (usada pelo arrastar-e-soltar).
export async function reorderGallery(
  items: Array<{ id: string; sort_order: number }>,
): Promise<void> {
  await apiPost("/functions/v1/admin-gallery?action=reorder", { items }, token());
}

// Categorias da galeria de fotos
export async function createGalleryCategory(name: string): Promise<void> {
  await apiPost("/functions/v1/admin-gallery?action=category-create", { name, sort_order: 0 }, token());
}

export async function updateGalleryCategory(id: number, name: string, sort_order: number): Promise<void> {
  await apiPut("/functions/v1/admin-gallery?action=category-update", { id, name, sort_order }, token());
}

export async function deleteGalleryCategory(id: number): Promise<void> {
  await apiDelete(`/functions/v1/admin-gallery?action=category-delete&id=${id}`, token());
}

// ---------- Vídeos ----------

export interface VideoItem {
  id: string;
  title: string;
  category: string;
  youtube_id: string;
  thumbnail: string;
  description: string | null;
  duration: string | null;
  sort_order: number;
  is_active: boolean;
  show_on_home: boolean;
  home_order: number;
  published_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
}

export interface VideoInput {
  id?: string;
  title: string;
  category: string;
  youtube_id: string;
  thumbnail: string;
  description: string | null;
  duration: string | null;
  sort_order: number;
  is_active: boolean;
  show_on_home: boolean;
  home_order: number;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
}

interface VideoListResponse {
  ok: boolean;
  count?: number;
  data?: VideoItem[];
}

export async function listVideos(): Promise<VideoItem[]> {
  const data = await apiGet<VideoListResponse>("/functions/v1/admin-videos", token());
  return data.data ?? [];
}

export async function createVideo(input: VideoInput): Promise<void> {
  await apiPost("/functions/v1/admin-videos", input, token());
  logActivity("video.criar", "videos", input.title);
}

export async function updateVideo(input: VideoInput): Promise<void> {
  await apiPut("/functions/v1/admin-videos", input, token());
  logActivity("video.editar", "videos", input.title);
}

export async function deleteVideo(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-videos?id=${encodeURIComponent(id)}`, token());
  logActivity("video.excluir", "videos", id);
}

// Baixa os vídeos do canal oficial do YouTube e cadastra os que ainda não existem.
// O dedupe é feito pelo ID do YouTube, então nunca repete vídeos. Retorna quantos foram adicionados.
export async function syncYouTubeVideos(): Promise<number> {
  const data = await apiGet<{ ok: boolean; added?: number }>("/functions/v1/get-videos?sync=1");
  return data.added ?? 0;
}

// Salva a nova ordem dos vídeos (usada pelo arrastar-e-soltar).
export async function reorderVideos(
  items: Array<{ id: string; sort_order: number }>,
): Promise<void> {
  await apiPost("/functions/v1/admin-videos?action=reorder", { items }, token());
}

// Preenche o SEO (título, descrição, palavras-chave) dos vídeos que ainda não têm.
export async function bulkOptimizeVideoSeo(): Promise<{ optimized: number }> {
  const data = await apiPost<{ ok: boolean; optimized?: number }>(
    "/functions/v1/admin-videos?action=bulk-seo",
    {},
    token(),
  );
  return { optimized: data.optimized ?? 0 };
}

// ---------- Hero (banners) ----------

export interface HeroSlide {
  id: string;
  image_url: string;
  image_url_mobile: string | null;
  title: string;
  subtitle: string | null;
  cta_text: string | null;
  cta_url: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface HeroInput {
  id?: string;
  image_url: string;
  image_url_mobile: string | null;
  title: string;
  subtitle: string | null;
  cta_text: string | null;
  cta_url: string | null;
  sort_order: number;
  is_active: boolean;
}

interface HeroListResponse {
  ok: boolean;
  count?: number;
  data?: HeroSlide[];
}

export async function listHero(): Promise<HeroSlide[]> {
  const data = await apiGet<HeroListResponse>("/functions/v1/admin-hero", token());
  return data.data ?? [];
}

export async function createHero(input: HeroInput): Promise<void> {
  await apiPost("/functions/v1/admin-hero", input, token());
  logActivity("slide.criar", "hero", input.title);
}

export async function updateHero(input: HeroInput): Promise<void> {
  await apiPut("/functions/v1/admin-hero", input, token());
  logActivity("slide.editar", "hero", input.title);
}

export async function deleteHero(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-hero?id=${encodeURIComponent(id)}`, token());
  logActivity("slide.excluir", "hero", id);
}

// Salva a nova ordem dos slides (usada pelo arrastar-e-soltar).
export async function reorderHero(
  items: Array<{ id: string; sort_order: number }>,
): Promise<void> {
  await apiPost("/functions/v1/admin-hero?action=reorder", { items }, token());
}

// ---------- FAQ ----------

export interface FaqItem {
  id: string;
  product_slug: string | null;
  question: string;
  answer: string;
  sort_order: number;
  is_active: boolean;
}

export interface FaqInput {
  id?: string;
  question: string;
  answer: string;
  product_slug: string | null;
  sort_order: number;
  is_active: boolean;
}

interface FaqListResponse {
  ok: boolean;
  count?: number;
  data?: FaqItem[];
}

export async function listFaqs(): Promise<FaqItem[]> {
  const data = await apiGet<FaqListResponse>("/functions/v1/admin-faq", token());
  return data.data ?? [];
}

export async function createFaq(input: FaqInput): Promise<void> {
  await apiPost("/functions/v1/admin-faq", input, token());
  logActivity("faq.criar", "faq", input.question);
}

export async function updateFaq(input: FaqInput): Promise<void> {
  await apiPut("/functions/v1/admin-faq", input, token());
  logActivity("faq.editar", "faq", input.question);
}

export async function deleteFaq(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-faq?id=${encodeURIComponent(id)}`, token());
  logActivity("faq.excluir", "faq", id);
}

// ---------- Usuários ----------

export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at?: string | null;
  /** Menus do painel liberados (null = acesso total). */
  permissions?: string[] | null;
}

export interface UserInput {
  name: string;
  email: string;
  password?: string;
  role: string;
  is_active?: boolean;
  /** Menus do painel liberados para editores. */
  permissions?: string[];
}

interface UserListResponse {
  ok: boolean;
  count?: number;
  data?: AdminUserRow[];
}

export async function listUsers(): Promise<AdminUserRow[]> {
  const data = await apiGet<UserListResponse>("/functions/v1/admin-users", token());
  return data.data ?? [];
}

export async function createUser(input: UserInput): Promise<void> {
  await apiPost("/functions/v1/admin-users", input, token());
  logActivity("usuario.criar", "usuarios", input.email);
}

export async function updateUser(input: {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  is_active?: boolean;
  password?: string;
  permissions?: string[];
}): Promise<void> {
  await apiPut("/functions/v1/admin-users", input, token());
  logActivity("usuario.editar", "usuarios", input.email ?? input.id);
}

export async function deleteUser(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-users?id=${encodeURIComponent(id)}`, token());
  logActivity("usuario.excluir", "usuarios", id);
}

// ---------- Exportação ----------

export async function exportData(format: "json" | "csv"): Promise<string> {
  const t = getToken();
  const res = await fetch(`${API_BASE}/functions/v1/export?format=${format}`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `Erro ${res.status}`);
  }
  return await res.text();
}

// ---------- SEO das páginas fixas ----------

export interface PageSeoRecord {
  page_slug: string;
  title: string;
  description: string;
  keywords: string | null;
  canonical_url: string | null;
}

interface PageSeoListResponse {
  ok: boolean;
  count?: number;
  data?: PageSeoRecord[];
}

export async function listPageSeo(): Promise<PageSeoRecord[]> {
  const data = await apiGet<PageSeoListResponse>("/functions/v1/admin-seo", token());
  return data.data ?? [];
}

export async function updatePageSeo(input: {
  page_slug: string;
  title: string;
  description: string;
  keywords: string;
  canonical_url: string;
}): Promise<void> {
  await apiPut("/functions/v1/admin-seo", input, token());
}

export async function bulkOptimizeSeo(): Promise<{ optimized: number }> {
  const data = await apiPost<{ ok: boolean; optimized?: number }>(
    "/functions/v1/admin-seo?action=bulk",
    {},
    token(),
  );
  return { optimized: data.optimized ?? 0 };
}

// Preenche o SEO das páginas que ainda não têm (título, descrição e palavras-chave).
export async function bulkOptimizePageSeo(): Promise<{ optimized: number }> {
  const data = await apiPost<{ ok: boolean; optimized?: number }>(
    "/functions/v1/admin-pages?action=bulk-seo",
    {},
    token(),
  );
  return { optimized: data.optimized ?? 0 };
}

export interface PageSeoBatchInput {
  slug: string;
  title: string;
  description: string;
  keywords: string;
  canonical_url: string;
}

// Grava de uma vez o SEO de várias páginas (botão "Preencher SEO").
export async function bulkSavePageSeo(items: PageSeoBatchInput[]): Promise<{ optimized: number }> {
  const data = await apiPost<{ ok: boolean; optimized?: number }>(
    "/functions/v1/admin-pages?action=bulk-save",
    { items },
    token(),
  );
  return { optimized: data.optimized ?? 0 };
}

// Dispara o seed idempotente de conteúdo (hero, FAQ, SEO de produtos e páginas).
// Seguro chamar várias vezes: preenche só o que estiver faltando.
export async function ensureCmsSeeded(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/functions/v1/cms-seed-content`, { method: "POST" });
    if (!res.ok) return false;
    const data = await res.json().catch(() => null);
    return data?.ok !== false;
  } catch {
    return false;
  }
}

// ---------- SEO Global ----------

export interface GlobalSeoData {
  site_url: string;
  favicon_url: string;
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  seo_og_image: string;
  seo_twitter: string;
}

const EMPTY_GLOBAL_SEO: GlobalSeoData = {
  site_url: "",
  favicon_url: "",
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  seo_og_image: "",
  seo_twitter: "",
};

interface GlobalSeoResponse {
  ok: boolean;
  data?: GlobalSeoData;
}

export async function getGlobalSeo(): Promise<GlobalSeoData> {
  const data = await apiGet<GlobalSeoResponse>("/functions/v1/admin-global-seo", token());
  return { ...EMPTY_GLOBAL_SEO, ...(data.data ?? {}) };
}

export async function updateGlobalSeo(input: Partial<GlobalSeoData>): Promise<GlobalSeoData> {
  const data = await apiPut<GlobalSeoResponse>("/functions/v1/admin-global-seo", input, token());
  logActivity("seo-global.editar", "seo-global", "");
  return { ...EMPTY_GLOBAL_SEO, ...(data.data ?? {}) };
}

// ---------- E-mail (SMTP + notificações de contato) ----------

export type SmtpEncryption = "ssl" | "starttls" | "none";

export interface SmtpConfigInput {
  host: string;
  port: number;
  encryption: SmtpEncryption;
  username: string;
  password: string;
  from_name: string;
  from_email: string;
}

export interface EmailConfig {
  recipient: string;
  smtp: {
    host: string;
    port: number;
    encryption: SmtpEncryption;
    username: string;
    password_set: boolean;
    from_name: string;
    from_email: string;
  };
  enabled: boolean;
  configured: boolean;
  from_domain: string | null;
}

interface EmailConfigResponse {
  ok: boolean;
  data?: EmailConfig;
}

export async function getEmailConfig(): Promise<EmailConfig> {
  const data = await apiGet<EmailConfigResponse>("/functions/v1/contact-email", token());
  return (
    data.data ?? {
      recipient: "",
      smtp: {
        host: "",
        port: 587,
        encryption: "starttls",
        username: "",
        password_set: false,
        from_name: "",
        from_email: "",
      },
      enabled: false,
      configured: false,
      from_domain: null,
    }
  );
}

export async function updateEmailConfig(input: {
  recipient: string;
  smtp: SmtpConfigInput;
  enabled: boolean;
}): Promise<void> {
  await apiPut("/functions/v1/contact-email", input, token());
}

export async function testEmail(to?: string): Promise<{ sent_to: string }> {
  const data = await apiPost<{ ok: boolean; data?: { sent_to: string } }>(
    "/functions/v1/contact-email",
    { action: "test", to: to ?? "" },
    token(),
  );
  return { sent_to: data.data?.sent_to ?? "" };
}

// ---------- Contatos (mensagens do site) ----------

export interface ContactRecord {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  tipo_embalagem: string | null;
  quantidade: string | null;
  assunto: string | null;
  mensagem: string | null;
  origem: string | null;
  status: string | null;
  created_at: string;
}

interface ContactListResponse {
  ok: boolean;
  count?: number;
  data?: ContactRecord[];
}

export async function listContacts(): Promise<ContactRecord[]> {
  const data = await apiGet<ContactListResponse>(
    "/functions/v1/contact-email?action=contacts",
    token(),
  );
  return data.data ?? [];
}

export async function deleteContacts(ids: string[]): Promise<void> {
  const query = ids.map((id) => encodeURIComponent(id)).join(",");
  await apiDelete(`/functions/v1/contact-email?ids=${query}`, token());
}

export async function markContacts(ids: string[], status: "novo" | "lido"): Promise<void> {
  await apiPost("/functions/v1/contact-email", { action: "mark", ids, status }, token());
}

// ---------- Aparência (logo + cores) ----------

export interface AppearanceData {
  logo_url: string;
  logo_url_light: string;
  logo_height: string;
  logo_height_mobile: string;
  logo_scale: string;
  logo_align: string;
  logo_bg: string;
  logo_radius: string;
  primary_color: string;
  accent_color: string;
  secondary_color: string;
  background_color: string;
  footer_bg: string;
  footer_text: string;
  font_heading: string;
  font_body: string;
  font_size_base: string;
  button_radius: string;
}

const EMPTY_APPEARANCE: AppearanceData = {
  logo_url: "",
  logo_url_light: "",
  logo_height: "",
  logo_height_mobile: "",
  logo_scale: "",
  logo_align: "",
  logo_bg: "",
  logo_radius: "",
  primary_color: "",
  accent_color: "",
  secondary_color: "",
  background_color: "",
  footer_bg: "",
  footer_text: "",
  font_heading: "",
  font_body: "",
  font_size_base: "",
  button_radius: "",
};

interface AppearanceResponse {
  ok: boolean;
  data?: AppearanceData;
}

export async function getAppearance(): Promise<AppearanceData> {
  const data = await apiGet<AppearanceResponse>("/functions/v1/admin-appearance", token());
  return { ...EMPTY_APPEARANCE, ...(data.data ?? {}) };
}

export async function updateAppearance(input: Partial<AppearanceData>): Promise<AppearanceData> {
  const data = await apiPut<AppearanceResponse>("/functions/v1/admin-appearance", input, token());
  logActivity("aparencia.editar", "aparencia", "");
  return { ...EMPTY_APPEARANCE, ...(data.data ?? {}) };
}

// ---------- Menu de navegação ----------

export interface MenuItem {
  id: string;
  label: string;
  url: string;
  parent_id: string | null;
  sort_order: number;
  is_active: boolean;
  is_external: boolean;
}

export interface MenuItemInput {
  id?: string;
  label: string;
  url: string;
  parent_id: string | null;
  sort_order: number;
  is_active: boolean;
  is_external: boolean;
}

interface MenuListResponse {
  ok: boolean;
  count?: number;
  data?: MenuItem[];
}

export async function listMenuItems(): Promise<MenuItem[]> {
  const data = await apiGet<MenuListResponse>("/functions/v1/admin-menus", token());
  return data.data ?? [];
}

export async function createMenuItem(input: MenuItemInput): Promise<void> {
  await apiPost("/functions/v1/admin-menus", input, token());
  logActivity("menu.criar", "menus", input.label);
}

export async function updateMenuItem(input: MenuItemInput): Promise<void> {
  await apiPut("/functions/v1/admin-menus", input, token());
  logActivity("menu.editar", "menus", input.label);
}

export async function deleteMenuItem(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-menus?id=${encodeURIComponent(id)}`, token());
  logActivity("menu.excluir", "menus", id);
}

export async function reorderMenuItems(
  items: Array<{ id: string; parent_id: string | null; sort_order: number }>,
): Promise<void> {
  await apiPost("/functions/v1/admin-menus?action=reorder", { items }, token());
}

// ---------- Páginas (conteúdo + SEO consolidado) ----------

export interface PageRecord {
  slug: string;
  title: string | null;
  content: string | null;
  is_active: boolean | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  canonical_url: string | null;
}

export interface PageInput {
  slug: string;
  title: string;
  content: string;
  is_active: boolean;
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  canonical_url: string;
}

interface PageListResponse {
  ok: boolean;
  count?: number;
  data?: PageRecord[];
}

export async function listPages(): Promise<PageRecord[]> {
  const data = await apiGet<PageListResponse>("/functions/v1/admin-pages", token());
  return data.data ?? [];
}

export async function updatePage(input: PageInput): Promise<void> {
  await apiPut("/functions/v1/admin-pages", input, token());
  logActivity("pagina.editar", "paginas", input.slug);
}

export async function createPage(input: PageInput): Promise<void> {
  await apiPost("/functions/v1/admin-pages", input, token());
  logActivity("pagina.criar", "paginas", input.slug);
}

export async function deletePage(slug: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-pages?slug=${encodeURIComponent(slug)}`, token());
  logActivity("pagina.excluir", "paginas", slug);
}

// ---------- Imagens das páginas ----------

export interface PageImagesMap {
  [slug: string]: Record<string, string>;
}

interface PageImagesResponse {
  ok: boolean;
  data?: PageImagesMap;
}

/** Todas as imagens configuradas, por página (slug). */
export async function listPageImages(): Promise<PageImagesMap> {
  const data = await apiGet<PageImagesResponse>("/functions/v1/page-images", token());
  return data.data ?? {};
}

/** Salva as imagens de uma página (junta com as que já existem). */
export async function savePageImages(
  slug: string,
  images: Record<string, string> | undefined,
): Promise<void> {
  await apiPut("/functions/v1/page-images", { slug, images: images ?? {} }, token());
  logActivity("pagina.imagens", "paginas", slug);
}

// ---------- Blocos da home (valores, tecnologia, estatísticas) ----------

export interface HomeBlock {
  id: string;
  section: string;
  title: string | null;
  description: string | null;
  icon: string | null;
  value: string | null;
  image_url: string | null;
  link_url: string | null;
  link_label: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface HomeSectionRecord {
  section: string;
  eyebrow: string | null;
  title: string | null;
  subtitle: string | null;
  sort_order?: number | null;
  is_active?: boolean | null;
  video_id?: string | null;
}

interface HomeListResponse {
  ok: boolean;
  blocks?: HomeBlock[];
  sections?: HomeSectionRecord[];
}

export async function listHomeBlocks(): Promise<HomeBlock[]> {
  const data = await apiGet<HomeListResponse>("/functions/v1/admin-home", token());
  return data.blocks ?? [];
}

export async function listHomeSections(): Promise<HomeSectionRecord[]> {
  const data = await apiGet<HomeListResponse>("/functions/v1/admin-home", token());
  return data.sections ?? [];
}

export async function createHomeBlock(input: {
  section: string;
  title?: string;
  description?: string;
  icon?: string;
  value?: string;
  image_url?: string;
  link_url?: string;
  link_label?: string;
  sort_order?: number;
}): Promise<void> {
  await apiPost("/functions/v1/admin-home", input, token());
}

export async function updateHomeBlock(input: {
  id: string;
  section?: string;
  title?: string;
  description?: string;
  icon?: string;
  value?: string;
  image_url?: string;
  link_url?: string;
  link_label?: string;
  is_active?: boolean;
}): Promise<void> {
  await apiPut("/functions/v1/admin-home", input, token());
}

export async function deleteHomeBlock(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-home?id=${encodeURIComponent(id)}`, token());
}

export async function reorderHomeBlocks(
  items: Array<{ id: string; sort_order: number }>,
): Promise<void> {
  await apiPost("/functions/v1/admin-home?action=reorder", { items }, token());
}

export async function upsertHomeSection(input: {
  section: string;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  video_id?: string;
  is_active?: boolean;
}): Promise<void> {
  await apiPut("/functions/v1/admin-home", { entity: "section", ...input }, token());
}

/** Salva a nova ordem das seções da home. */
export async function reorderHomeSections(
  items: Array<{ section: string; sort_order: number }>,
): Promise<void> {
  await apiPost("/functions/v1/admin-home?action=reorder-sections", { items }, token());
}

// ---------- Conteúdo da página Empresa (seções + blocos) ----------
// Reutiliza o mesmo formato dos blocos da home, mas com chaves "emp_".

export type EmpresaBlock = HomeBlock;
export type EmpresaSectionRecord = HomeSectionRecord;

interface EmpresaListResponse {
  ok: boolean;
  blocks?: HomeBlock[];
  sections?: HomeSectionRecord[];
}

export async function listEmpresaBlocks(): Promise<HomeBlock[]> {
  const data = await apiGet<EmpresaListResponse>("/functions/v1/admin-empresa", token());
  return data.blocks ?? [];
}

export async function listEmpresaSections(): Promise<HomeSectionRecord[]> {
  const data = await apiGet<EmpresaListResponse>("/functions/v1/admin-empresa", token());
  return data.sections ?? [];
}

export async function createEmpresaBlock(input: {
  section: string;
  title?: string;
  description?: string;
  icon?: string;
  value?: string;
  image_url?: string;
  link_url?: string;
  link_label?: string;
  sort_order?: number;
}): Promise<void> {
  await apiPost("/functions/v1/admin-empresa", input, token());
}

export async function updateEmpresaBlock(input: {
  id: string;
  section?: string;
  title?: string;
  description?: string;
  icon?: string;
  value?: string;
  image_url?: string;
  link_url?: string;
  link_label?: string;
  is_active?: boolean;
}): Promise<void> {
  await apiPut("/functions/v1/admin-empresa", input, token());
}

export async function deleteEmpresaBlock(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-empresa?id=${encodeURIComponent(id)}`, token());
}

export async function reorderEmpresaBlocks(
  items: Array<{ id: string; sort_order: number }>,
): Promise<void> {
  await apiPost("/functions/v1/admin-empresa?action=reorder", { items }, token());
}

export async function upsertEmpresaSection(input: {
  section: string;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  is_active?: boolean;
}): Promise<void> {
  await apiPut("/functions/v1/admin-empresa", { entity: "section", ...input }, token());
}

export async function reorderEmpresaSections(
  items: Array<{ section: string; sort_order: number }>,
): Promise<void> {
  await apiPost("/functions/v1/admin-empresa?action=reorder-sections", { items }, token());
}

// ---------- Backup & Restauração ----------

export interface BackupTableStatus {
  name: string;
  count: number;
}

interface BackupStatusResponse {
  ok: boolean;
  tables?: BackupTableStatus[];
}

export interface BackupSummary {
  format?: string;
  version?: number;
  exported_at?: string;
  tables: Record<string, { rows?: unknown[]; columns?: Array<{ name: string; type: string }> }>;
}

interface BackupImportResponse {
  ok: boolean;
  restored?: { tables: number; rows: number };
}

export async function backupStatus(): Promise<BackupTableStatus[]> {
  const data = await apiGet<BackupStatusResponse>("/functions/v1/admin-backup?action=status", token());
  return data.tables ?? [];
}

// Baixa o backup completo como texto JSON (para salvar em arquivo).
export async function exportBackupText(): Promise<string> {
  const t = getToken();
  const res = await fetch(`${API_BASE}/functions/v1/admin-backup`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `Erro ${res.status}`);
  }
  return await res.text();
}

export async function importBackup(backup: BackupSummary): Promise<{ tables: number; rows: number }> {
  const data = await apiPost<BackupImportResponse>("/functions/v1/admin-backup", backup, token());
  return { tables: data.restored?.tables ?? 0, rows: data.restored?.rows ?? 0 };
}

// ---------- Backups salvos no painel ----------

export interface BackupSnapshot {
  id: string;
  name: string;
  created_by: string | null;
  created_at: string;
  size: number;
}

export async function listBackups(): Promise<BackupSnapshot[]> {
  const data = await apiGet<{ ok: boolean; data?: BackupSnapshot[] }>("/functions/v1/admin-backup?action=list", token());
  return data.data ?? [];
}

export async function saveBackup(): Promise<BackupSnapshot> {
  const data = await apiPost<{ ok: boolean; data?: BackupSnapshot }>("/functions/v1/admin-backup?action=save", {}, token());
  if (!data.data) throw new Error("Não foi possível salvar o backup");
  return data.data;
}

export async function restoreBackup(id: string): Promise<{ tables: number; rows: number }> {
  const data = await apiPost<{ ok: boolean; restored?: { tables: number; rows: number } }>("/functions/v1/admin-backup?action=restore", { id }, token());
  return { tables: data.restored?.tables ?? 0, rows: data.restored?.rows ?? 0 };
}

export async function deleteBackup(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-backup?action=delete&id=${encodeURIComponent(id)}`, token());
}

export async function clearBackups(): Promise<void> {
  await apiDelete("/functions/v1/admin-backup?action=clear", token());
}

// ---------- Troca de conexão do banco ----------

export interface ConnectionTestResult {
  ok: boolean;
  error?: string;
  database?: string | null;
  time?: string | null;
  host?: string | null;
  cms_tables?: number;
}

export async function testConnection(connectionString: string): Promise<ConnectionTestResult> {
  try {
    const data = await apiPost<{
      ok: boolean;
      database?: string | null;
      time?: string | null;
      host?: string | null;
      cms_tables?: number;
    }>(
      "/functions/v1/admin-backup?action=test",
      { connection_string: connectionString },
      token(),
    );
    return { ok: true, ...data };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

// ---------- Analytics (visitas do site) ----------

export interface AnalyticsDaily {
  date: string;
  count: number;
}

export interface AnalyticsData {
  today: number;
  last7: number;
  last30: number;
  total: number;
  range: number;
  start?: string | null;
  end?: string | null;
  daily: AnalyticsDaily[];
  top_paths: Array<{ path: string; count: number }>;
  devices: Array<{ device: string; count: number }>;
  referrers: Array<{ referrer: string; count: number }>;
}

const EMPTY_ANALYTICS = (range: number): AnalyticsData => ({
  today: 0,
  last7: 0,
  last30: 0,
  total: 0,
  range,
  daily: [],
  top_paths: [],
  devices: [],
  referrers: [],
});

export async function getAnalytics(
  params: { range?: number; start?: string; end?: string } = {},
): Promise<AnalyticsData> {
  const qs = new URLSearchParams();
  if (params.start && params.end) {
    qs.set("start", params.start);
    qs.set("end", params.end);
  } else {
    qs.set("range", String(params.range ?? 30));
  }
  const data = await apiGet<{ ok: boolean; data?: AnalyticsData }>(
    `/functions/v1/analytics?${qs.toString()}`,
    token(),
  );
  return { ...EMPTY_ANALYTICS(params.range ?? 30), ...(data.data ?? {}) };
}

export interface AnalyticsVisit {
  id: string;
  path: string;
  referrer: string | null;
  device: string | null;
  ip: string | null;
  created_at: string;
}

/** Lista as visitas recentes (registros individuais) para seleção/exclusão. */
export async function listAnalyticsVisits(limit = 100): Promise<AnalyticsVisit[]> {
  const data = await apiGet<{ ok: boolean; data?: AnalyticsVisit[] }>(
    `/functions/v1/analytics?action=visits&limit=${limit}`,
    token(),
  );
  return data.data ?? [];
}

/** Exclui visitas selecionadas do Analytics. */
export async function deleteAnalyticsVisits(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const list = ids.map((id) => encodeURIComponent(id)).join(",");
  await apiDelete(`/functions/v1/analytics?ids=${list}`, token());
}

/** Apaga todas as visitas registradas no Analytics. */
export async function clearAnalytics(): Promise<void> {
  await apiDelete("/functions/v1/analytics?action=clear", token());
}

// ---------- Logs de atividade do painel ----------

export interface ActivityLog {
  id: string;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  action: string;
  entity: string | null;
  entity_id: string | null;
  detail: string | null;
  ip: string | null;
  created_at: string;
}

// Registra uma ação feita no painel (fire-and-forget).
export async function logActivity(action: string, entity?: string, detail?: string): Promise<void> {
  const t = getToken();
  if (!t) return;
  try {
    await apiPost("/functions/v1/admin-logs", { action, entity, detail }, t);
  } catch {
    // silencioso — não deve atrapalhar a operação principal
  }
}

export async function listLogs(limit = 200): Promise<ActivityLog[]> {
  const data = await apiGet<{ ok: boolean; data?: ActivityLog[] }>(`/functions/v1/admin-logs?limit=${limit}`, token());
  return data.data ?? [];
}

export async function deleteLog(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-logs?id=${encodeURIComponent(id)}`, token());
}

export async function clearLogs(): Promise<void> {
  await apiDelete("/functions/v1/admin-logs?action=clear", token());
}

/** Exclui vários logs de uma vez (selecionados na lista). */
export async function deleteLogs(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const list = ids.map((id) => encodeURIComponent(id)).join(",");
  await apiDelete(`/functions/v1/admin-logs?ids=${list}`, token());
}

// ---------- Redirecionamentos (301/302) ----------

export interface RedirectRecord {
  id: string;
  from_path: string;
  to_path: string;
  status_code: number;
  is_active: boolean;
  hits: number;
  notes: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface RedirectInput {
  id?: string;
  from_path: string;
  to_path: string;
  status_code: number;
  is_active: boolean;
  notes: string | null;
}

interface RedirectListResponse {
  ok: boolean;
  count?: number;
  data?: RedirectRecord[];
}

export async function listRedirects(): Promise<RedirectRecord[]> {
  const data = await apiGet<RedirectListResponse>("/functions/v1/admin-redirects", token());
  return data.data ?? [];
}

export async function createRedirect(input: RedirectInput): Promise<void> {
  await apiPost("/functions/v1/admin-redirects", input, token());
  logActivity("redirecionamento.criar", "redirecionamentos", input.from_path);
}

export async function updateRedirect(input: RedirectInput): Promise<void> {
  await apiPut("/functions/v1/admin-redirects", input, token());
  logActivity("redirecionamento.editar", "redirecionamentos", input.from_path);
}

export async function deleteRedirect(id: string): Promise<void> {
  await apiDelete(`/functions/v1/admin-redirects?id=${encodeURIComponent(id)}`, token());
  logActivity("redirecionamento.excluir", "redirecionamentos", id);
}

export interface RedirectBatchResult {
  inserted: number;
  skipped: number;
  errors: string[];
}

// Importa várias regras de redirecionamento de uma vez (pula as que já existem).
export async function importRedirects(items: RedirectInput[]): Promise<RedirectBatchResult> {
  const data = await apiPost<{ ok: boolean; data?: RedirectBatchResult }>(
    "/functions/v1/admin-redirects?action=batch",
    { items },
    token(),
  );
  const result = {
    inserted: data.data?.inserted ?? 0,
    skipped: data.data?.skipped ?? 0,
    errors: data.data?.errors ?? [],
  };
  logActivity(
    "redirecionamento.importar",
    "redirecionamentos",
    `${result.inserted} regra(s) importada(s)`,
  );
  return result;
}